import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CardScreen, InstructionStep } from './common/CardScreen';
import { t } from "@/strings/index";
import { ROUTES } from '@/constants/routes';
import {
  logScreenRender,
  logButtonClick,
  logBackNavigation,
  completeScenario,
  endPolicyEvaluationTiming,
  startPageRenderTiming,
  endPageRenderTiming
} from '../telemetry/telemetryEvents';
import { logDev as debugLog } from '@/logging/devConsole';
import { logoutWithRedirect } from '@/utils/authService';
import teamsLogo from '@/assets/teams.svg';

const PLAYSTORE_URL = 'https://play.google.com/store/apps/details?id=com.microsoft.teams';
const APPSTORE_URL = 'https://apps.apple.com/app/microsoft-teams/id1113153706';

const AppDownloadScreen: React.FC = () => {
  // Start timing page render immediately
  startPageRenderTiming('app-download');
  
  const location = useLocation();
  const navigate = useNavigate();

  // Determine platform from sessionStorage (persisted in DeviceSelection)
  const platform = (sessionStorage.getItem('selectedDevice') as 'android' | 'ios') || 'unknown';
  debugLog('[AppDownloadScreen] Platform from sessionStorage:', platform);

  // Determine if this is the first screen after auth redirect
  const isFirstScreenAfterAuth = (location.state as any)?.isFirstScreenAfterAuth ?? false;

  useEffect(() => {
    // Build UserBI data with MFA and MAM policy details (if they skipped/failed)
    const userbiData: Record<string, any> = {};
    
    const mfaResult = (location.state as any)?.mfaResult;
    const mamResult = (location.state as any)?.mamResult;
    
    // Include MFA policy data if available
    if (mfaResult) {
      if (typeof mfaResult.isRequired === 'boolean') {
        userbiData.mfaPolicyIsRequired = mfaResult.isRequired;
      }
      if (mfaResult.policyType) {
        userbiData.mfaPolicyType = mfaResult.policyType;
      }
      if (mfaResult.reason) {
        userbiData.mfaPolicyReason = mfaResult.reason;
      }
      if (mfaResult.details) {
        userbiData.mfaPolicyDetails = mfaResult.details;
      }
    }
    
    // Include MAM policy data if available
    if (mamResult) {
      if (typeof mamResult.isRequired === 'boolean') {
        userbiData.mamPolicyIsRequired = mamResult.isRequired;
      }
      if (mamResult.policyType) {
        userbiData.mamPolicyType = mamResult.policyType;
      }
      if (mamResult.reason) {
        userbiData.mamPolicyReason = mamResult.reason;
      }
      if (mamResult.details) {
        userbiData.mamPolicyDetails = mamResult.details;
      }
    }
    
    // Log screen view (UserBI + marks 'app-download' step)
    logScreenRender('AppDownloadScreen', Object.keys(userbiData).length > 0 ? userbiData : undefined);
    endPageRenderTiming('app-download');
    endPolicyEvaluationTiming('mam');
  }, [location.state]);

  const storeUrl = platform === 'android'
    ? PLAYSTORE_URL
    : platform === 'ios'
      ? APPSTORE_URL
      : '';

  debugLog('Selected store URL:', storeUrl);
  debugLog('Selected platform:', platform);
  
  const handleDone = async () => {
    debugLog('[AppDownloadScreen] Done clicked - clearing MSAL session and completing scenario');
    logButtonClick('AppDownloadScreen', 'done');

    // Complete scenario BEFORE redirect to ensure telemetry is captured
    // This sends the final scenario event to OneCollector
    completeScenario('success');
    debugLog('[AppDownloadScreen] Scenario completed successfully.');

    // Set flag to prevent abandonment tracking during logout redirect
    // useAbandonmentTracking hook checks this flag in beforeunload/unload handlers
    sessionStorage.setItem('scenarioCompleted', 'true');

    // Log out from Azure AD and redirect to feedback-thanks screen
    const postLogoutUri = window.location.origin + ROUTES.FEEDBACK_THANKS;
    debugLog('[AppDownloadScreen] Logout redirect URI:', postLogoutUri);
    await logoutWithRedirect(postLogoutUri);
  };

  const handleBack = () => {
    logBackNavigation('AppDownloadScreen');
    navigate(-1);
  };

  const instructions: InstructionStep[] = [
    { number: 1, text: t('appDownload.step1') },
    { number: 2, text: t('appDownload.step2') }
  ];

  return (
    <CardScreen
      title={t('appDownload.title')}
      qrCodeUrl={storeUrl}
      qrLogoUrl={teamsLogo}
      instructions={instructions}
      primaryButtonText={t('appDownload.done')}
      onPrimaryButtonClick={handleDone}
      secondaryLinkText={t('appDownload.cantScan')}
      secondaryLinkUrl={storeUrl}
      onBackClick={handleBack}
      showBackButton={!isFirstScreenAfterAuth}
    />
  );
};

export default AppDownloadScreen;
